// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import createTypedReducer, { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export interface PaymentInfo {
    lastFour: string;
    cardType: string;
    expirationDate: string;
    poNumber: string;
    job: string;
    paymentMethodName: string;
    description: string;
}

type CheckoutState = Readonly<{
    step: number;
    stepsTotal: number;
    nextButtonDisabled?: boolean;
    cart: PaymentInfo;
    firstAvailableDate: string;
    blackoutDays: [];
    shipComplete?: string;
}>;

const initialCheckoutState: CheckoutState = {
    step: 1,
    stepsTotal: 4,
    nextButtonDisabled: false,
    cart: {
        lastFour: "",
        cardType: "",
        expirationDate: "",
        poNumber: "",
        job: "",
        paymentMethodName: "",
        description: "",
    },
    firstAvailableDate: "",
    blackoutDays: [],
};

const xCheckout = createTypedReducerWithImmer(initialCheckoutState, {
    "Checkout/Stepper": (draft: Draft<CheckoutState>, action: { step: number }) => {
        draft.step = action.step;
    },

    "Checkout/validationStepper": (draft: Draft<CheckoutState>, action: { nextButtonDisabled?: boolean }) => {
        draft.nextButtonDisabled = action.nextButtonDisabled ?? false;
    },

    "Checkout/stepsTotal": (draft: Draft<CheckoutState>, action: { stepsTotal: number }) => {
        draft.stepsTotal = action.stepsTotal;
    },

    "Checkout/updateCardInfo": (draft: Draft<CheckoutState>, action: { lastFour: string; cardType: string }) => {
        draft.cart.lastFour = action.lastFour;
        draft.cart.cardType = action.cardType;
    },

    "Checkout/updateCardDescription": (draft: Draft<CheckoutState>, action: { description: string }) => {
        draft.cart.description = action.description;
    },

    "Checkout/updateExpirationDate": (draft: Draft<CheckoutState>, action: { expirationDate: string }) => {
        draft.cart.expirationDate = action.expirationDate;
    },

    "Checkout/updatePoNumber": (draft: Draft<CheckoutState>, action: { poNumber: string }) => {
        draft.cart.poNumber = action.poNumber;
    },

    "Checkout/updateJob": (draft: Draft<CheckoutState>, action: { job: string }) => {
        draft.cart.job = action.job;
    },
    "Checkout/updatePaymentMethodName": (draft: Draft<CheckoutState>, action: { paymentMethodName: string }) => {
        draft.cart.paymentMethodName = action.paymentMethodName;
    },
    "Checkout/LoadFirstAvailableDate": (draft: Draft<CheckoutState>, action: { firstAvailableDate: string }) => {
        draft.firstAvailableDate = action.firstAvailableDate;
    },
    "Checkout/LoadBlackOutDays": (draft: Draft<CheckoutState>, action: { blackoutDays: [] }) => {
        draft.blackoutDays = action.blackoutDays;
    },
    "Checkout/updateShipComplete": (draft: Draft<CheckoutState>, action: { shipComplete: string }) => {
        draft.shipComplete = action.shipComplete;
    },
});

(reducers as any).xCheckout = xCheckout;

export type xCheckoutState = {
    xCheckout: CheckoutState;
} & ApplicationState;

type xCheckoutReducers = {
    xCheckout: typeof xCheckout;
};

export type xCheckoutActions = Parameters<xCheckoutReducers[keyof xCheckoutReducers]>[1] | AnyAction;
